import { useCallback, useEffect, useRef, useState } from "react";
import { Smartphone } from "lucide-react";
import { PageHeader } from "@/components/data-display/page-header";
import { Badge } from "@/components/ui/badge";
import { InfoBanner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { EmptyState } from "@/components/ui/feedback";
import { Table, TableWrapper } from "@/components/ui/table";
import { ErrorState, LoadingState, PermissionDeniedState } from "@/components/ui/system-state";
import { useSignerPairingAllowed } from "@/hooks/use-permission";
import { withStepUpRetry } from "@/lib/api";
import {
  approveSignerRebind,
  fetchSignerDevices,
  fetchSignerRebinds,
  type SignerDevice,
  type SignerRebind,
} from "@/lib/signer-devices-api";

/**
 * W-3 "Signer devices" (/admin/signer-devices, SGN-110, ALL-5839 lane f15).
 *
 * Two live reads and one existing mutation, all browser-session only:
 * the queue of device re-binds waiting for a second admin's approval, and a
 * lookup of one named person's devices. The Approve button is gated on the
 * real `auth_roles:manage` scope (useSignerPairingAllowed) AND a loaded API
 * contract; the server stays the authority — this only hides a control the
 * gateway would refuse anyway (REBIND_SELF_APPROVAL, PRINCIPAL_NOT_HUMAN,
 * AUTH_INSUFFICIENT_SCOPE). There is deliberately no reject/revoke action
 * here. Every value shown comes from the API; nothing is fabricated.
 */

/** Plain sentence per refusal reason code, from the lane contract. Codes not
 * listed fall back to the gateway's own message, never a made-up one. */
export const REBIND_DENIAL_SENTENCES: Record<string, string> = {
  REBIND_SELF_APPROVAL: "You proposed this re-bind, so a second admin must approve it.",
  PRINCIPAL_NOT_HUMAN: "Only a named person can approve; service accounts and API keys cannot.",
  REBIND_WINDOW_EXPIRED: "The approval window has closed; ask for a new request.",
  REBIND_ALREADY_DECIDED: "Someone already decided this request.",
  BINDING_STATE_CONFLICT: "The wallet's signer moved since this was proposed; reload.",
  AUTH_INSUFFICIENT_SCOPE: "You don't have permission to approve signer device changes. Ask an organization admin.",
};

/** A refusal sentence for a decision: the mapped plain sentence when the
 * reason code is known, else the gateway's own message, else a generic line. */
export function rebindDenialText(reasonCode?: string, fallback?: string): string {
  if (reasonCode && REBIND_DENIAL_SENTENCES[reasonCode]) return REBIND_DENIAL_SENTENCES[reasonCode];
  return fallback || "The approval could not be completed. Try again, or ask an organization admin.";
}

/** Relative time to `expiresAt`, or "Closed" once it has passed. Never a
 * fabricated value: an unparseable timestamp renders as an em dash. */
export function rebindExpiryLabel(expiresAt: string, now: number): string {
  const t = new Date(expiresAt).getTime();
  if (!Number.isFinite(t)) return "—";
  if (t <= now) return "Closed";
  const minutes = Math.round((t - now) / 60_000);
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (hours < 24) return rem ? `in ${hours}h ${rem}m` : `in ${hours}h`;
  const days = Math.floor(hours / 24);
  return `in ${days} day${days === 1 ? "" : "s"}`;
}

export function deviceStateTone(state: string): "success" | "danger" | "pending" | "neutral" {
  switch ((state || "").toUpperCase()) {
    case "ACTIVE":
    case "BOUND":
      return "success";
    case "REVOKED":
      return "danger";
    case "PENDING":
      return "pending";
    default:
      return "neutral";
  }
}

function timestamp(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function dash(value: string | undefined): string {
  return value && value.trim() ? value : "—";
}

export default function SignerDevicesPage() {
  const allowed = useSignerPairingAllowed();

  const [queue, setQueue] = useState<SignerRebind[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState<{ message: string; supportReference?: string; capability?: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [approvedId, setApprovedId] = useState<string | null>(null);
  const [denial, setDenial] = useState<{ id: string; message: string; supportReference?: string; capability?: string } | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const [principalId, setPrincipalId] = useState("");
  const [devices, setDevices] = useState<SignerDevice[] | null>(null);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState<{ message: string; supportReference?: string; capability?: string } | null>(null);

  const requestSeq = useRef(0);

  const loadQueue = useCallback(async () => {
    const seq = ++requestSeq.current;
    setQueueLoading(true);
    setQueueError(null);
    const result = await fetchSignerRebinds({ limit: 50 });
    if (seq !== requestSeq.current) return;
    setQueue(result.items);
    setQueueError(result.error ? { message: result.error, supportReference: result.supportReference, capability: result.capability } : null);
    setQueueLoading(false);
  }, []);

  useEffect(() => {
    if (!allowed) {
      setQueueLoading(false);
      return;
    }
    void loadQueue();
  }, [allowed, loadQueue]);

  // Live relative time for the expiry column, refreshed on a timer so the
  // "Closed" transition is honest rather than frozen at render time.
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(handle);
  }, []);

  async function handleApprove(id: string) {
    setBusyId(id);
    setDenial(null);
    setApprovedId(null);
    const outcome = await withStepUpRetry(() => approveSignerRebind(id));
    setBusyId(null);
    if (outcome.result.ok) {
      setApprovedId(id);
      void loadQueue();
      return;
    }
    setDenial({
      id,
      message: rebindDenialText(outcome.result.reasonCode, outcome.result.error),
      supportReference: outcome.result.supportReference ?? outcome.stepUpSupportReference,
      capability: outcome.result.capability,
    });
  }

  async function handleLookup(event: React.FormEvent) {
    event.preventDefault();
    const id = principalId.trim();
    if (!id) {
      // The relay requires a principal_id; never issue the call without one.
      setDevices(null);
      setDevicesError({ message: "Enter a person's principal id to look up their devices." });
      return;
    }
    setDevicesLoading(true);
    setDevicesError(null);
    const result = await fetchSignerDevices(id);
    setDevices(result.items);
    setDevicesError(result.error ? { message: result.error, supportReference: result.supportReference, capability: result.capability } : null);
    setDevicesLoading(false);
  }

  if (!allowed) {
    return (
      <div>
        <PageHeader title="Signer devices" />
        <PermissionDeniedState
          title="You can't manage signer devices"
          description="Approving a new signer device needs the auth_roles:manage scope, which an organization admin holds. Ask an organization admin to approve this change."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Signer devices"
        subtitle="Approve a pending replacement device for a wallet, and look up the devices bound to one person."
      />

      <Card className="mb-5">
        <CardBody>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="size-4 text-primary" /> Waiting for approval
          </CardTitle>

          {approvedId && (
            <InfoBanner variant="success" className="mt-3">
              Request {approvedId} approved. The new device can now be paired.
            </InfoBanner>
          )}

          {denial && (
            <InfoBanner variant="danger" className="mt-3">
              {denial.message}
              <SupportDetails reference={denial.supportReference} capability={denial.capability} />
            </InfoBanner>
          )}

          {queueLoading && <LoadingState rows={3} />}

          {!queueLoading && queueError && (
            <div data-testid="signer-rebinds-error">
              <ErrorState
                title="Couldn't load the approval queue"
                description={queueError.message}
                onRetry={() => void loadQueue()}
              />
              <SupportDetails reference={queueError.supportReference} capability={queueError.capability} />
            </div>
          )}

          {!queueLoading && !queueError && queue.length === 0 && (
            <EmptyState
              title="No devices are waiting for approval"
              description="When an admin proposes replacing a wallet's signer device, the request appears here for a second admin to approve."
            />
          )}

          {!queueLoading && !queueError && queue.length > 0 && (
            <TableWrapper>
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg">
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">New device</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Replaces</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Wallet</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Reason</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Requested by</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Requested</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Expires</th>
                    <th className="px-4 py-[9px] text-right text-[10.5px] font-semibold normal-case tracking-normal text-muted">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map((row) => (
                    <tr key={row.id} className="border-b border-line last:border-b-0" data-testid={`signer-rebind-row-${row.id}`}>
                      <td className="px-4 py-[9px] whitespace-nowrap font-mono text-[11.5px] text-ink">{dash(row.toDeviceId)}</td>
                      <td className="px-4 py-[9px] whitespace-nowrap font-mono text-[11.5px] text-muted">{dash(row.fromDeviceId)}</td>
                      <td className="px-4 py-[9px] whitespace-nowrap font-mono text-[11.5px] text-ink">{dash(row.walletId)}</td>
                      <td className="px-4 py-[9px] text-[11.5px] text-ink">{dash(row.reason)}</td>
                      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">{dash(row.requestedBy)}</td>
                      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">{timestamp(row.requestedAt)}</td>
                      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">{rebindExpiryLabel(row.expiresAt, now)}</td>
                      <td className="px-4 py-[9px] text-right">
                        {row.state === "PENDING" ? (
                          <Button
                            size="sm"
                            loading={busyId === row.id}
                            disabled={busyId !== null}
                            onClick={() => void handleApprove(row.id)}
                          >
                            Approve
                          </Button>
                        ) : (
                          <span className="text-[11.5px] text-muted">{dash(row.state)}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <CardTitle>Look up a person&apos;s devices</CardTitle>
          <p className="mt-2 text-xs text-muted">
            Enter a person&apos;s principal id to see the devices bound to them. The lookup always names one person.
          </p>
          <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={handleLookup}>
            <label className="flex flex-col gap-1 text-xs text-muted">
              Principal id
              <input
                type="text"
                value={principalId}
                onChange={(e) => setPrincipalId(e.target.value)}
                placeholder="prn_..."
                className="h-[36px] w-[280px] max-w-full rounded-[8px] border border-line bg-surface px-3 text-[13px] text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
              />
            </label>
            <Button type="submit" variant="secondary" loading={devicesLoading} disabled={!principalId.trim()}>
              Look up
            </Button>
          </form>

          {devicesError && (
            <InfoBanner variant="danger" className="mt-3">
              {devicesError.message}
              <SupportDetails reference={devicesError.supportReference} capability={devicesError.capability} />
            </InfoBanner>
          )}

          {devices && !devicesError && devices.length === 0 && (
            <EmptyState
              title="No devices found"
              description="This person has no signer devices bound in this organization."
            />
          )}

          {devices && !devicesError && devices.length > 0 && (
            <TableWrapper>
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg">
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Device</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Wallet</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">State</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Bound at</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Revoked at</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => (
                    <tr key={device.deviceId} className="border-b border-line last:border-b-0">
                      <td className="px-4 py-[9px] whitespace-nowrap font-mono text-[11.5px] text-ink">{dash(device.deviceId)}</td>
                      <td className="px-4 py-[9px] whitespace-nowrap font-mono text-[11.5px] text-ink">{dash(device.walletId)}</td>
                      <td className="px-4 py-[9px] whitespace-nowrap">
                        <Badge variant={deviceStateTone(device.state)} size="sm">{dash(device.state)}</Badge>
                      </td>
                      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">{timestamp(device.boundAt)}</td>
                      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">{device.revokedAt ? timestamp(device.revokedAt) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
